import React from "react";
import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";

const Step = ({ n, title, body }) => (
  <div className="relative pl-12">
    <div className="absolute left-0 top-0 w-9 h-9 rounded-full bg-emerald-700 text-white font-display font-bold flex items-center justify-center">
      {n}
    </div>
    <h3 className="font-display font-bold text-lg text-gray-900">{title}</h3>
    <p className="text-gray-600 mt-1.5 leading-relaxed">{body}</p>
  </div>
);

export default function HowItWorks() {
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24">
      <div className="text-xs uppercase tracking-[0.2em] text-emerald-700 font-semibold mb-2">How it works</div>
        <h1 className="font-display font-bold text-4xl lg:text-5xl tracking-tight text-gray-900">
        Earn cashback in three steps. <br /> Keep more of every dollar.
      </h1>
      <p className="text-gray-700 mt-4 max-w-2xl">
        Affiliate networks pay merchants for sending them customers. LoyaltyCircle AI splits that
        commission with you — 50% goes back to your wallet on every verified purchase.
      </p>

      <div className="mt-12 space-y-8">
        <Step n="1" title="Tell Ria about your life" body="Share your household size, favorite stores, and budget goals. The more Ria knows, the smarter your deal feed becomes." />
        <Step n="2" title="Shop through LoyaltyCircle" body="Browse offers or ask Ria. Click any deal to activate cashback and shop on the merchant's site as you normally would." />
        <Step n="3" title="Get cashback on confirmed purchases" body="When the merchant confirms your purchase (typically 30 days), 50% of the affiliate commission is added to your wallet." />
      </div>

      <div className="mt-16 grid md:grid-cols-2 gap-6">
        <div className="rounded-2xl border border-emerald-100 bg-emerald-50/40 p-6">
          <h3 className="font-display font-bold text-lg">The 50/50 split, in plain numbers</h3>
          <ul className="mt-3 space-y-2 text-sm text-gray-700">
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" /> You buy $100 of groceries through Walmart with a 10% affiliate offer.</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" /> Walmart pays a $10 commission to LoyaltyCircle.</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" /> $5 goes to your cashback wallet. $5 stays with the platform.</li>
            <li className="flex items-start gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5" /> If you joined via a friend's link, $1 of the platform's $5 goes to them.</li>
          </ul>
        </div>
        <div className="rounded-2xl border border-amber-100 bg-amber-50/40 p-6">
          <h3 className="font-display font-bold text-lg">What we don't do</h3>
          <ul className="mt-3 space-y-2 text-sm text-gray-700">
            <li>· No multi-level recruitment chains.</li>
            <li>· No referral rewards for sign-ups alone — only verified purchases.</li>
            <li>· No "guaranteed earnings" or investment products.</li>
            <li>· Cashback is always tied to real affiliate commissions confirmed by merchants.</li>
          </ul>
        </div>
      </div>

      <div className="mt-16 text-center">
        <Link
          to="/signup"
          data-testid="how-cta-signup"
          className="inline-flex items-center justify-center bg-emerald-700 hover:bg-emerald-800 text-white rounded-full px-6 py-3 text-sm font-semibold transition-colors"
        >
          Start earning cashback <ArrowRight className="w-4 h-4 ml-2" />
        </Link>
      </div>
    </div>
  );
}
